/**
 * Runtime detection and small platform helpers for the desktop native
 * shell.
 *
 * Electron (the desktop shell) exposes a `window.electron` API via a
 * contextBridge preload (see `quadratic-electron/src/preload.ts`).
 * `isNative()` returns true when running inside it.
 */

// Mirrors `quadratic-electron/src/api.ts`. Duplicated here (rather than
// imported across workspaces) so the renderer keeps a clean dep graph.
export type ElectronUpdateInfo = {
  version: string;
  releaseDate?: string;
  releaseName?: string;
  releaseNotes?: string;
};

export type ElectronUpdateProgress = {
  percent: number;
  bytesPerSecond: number;
  transferred: number;
  total: number;
};

export type ElectronUpdateEvent =
  | { type: 'checking-for-update' }
  | { type: 'update-available'; info: ElectronUpdateInfo }
  | { type: 'update-not-available'; info: ElectronUpdateInfo }
  | { type: 'download-progress'; progress: ElectronUpdateProgress }
  | { type: 'update-downloaded'; info: ElectronUpdateInfo }
  | { type: 'error'; message: string };

export type NativeFetchRequest = {
  url: string;
  method: string;
  headers: Array<[string, string]>;
  body?: ArrayBuffer;
};

export type NativeFetchResponse = {
  status: number;
  statusText: string;
  headers: Array<[string, string]>;
  body: ArrayBuffer;
};

export type ElectronApi = {
  platform: NodeJS.Platform;
  appVersion: string;
  fetch: (req: NativeFetchRequest) => Promise<NativeFetchResponse>;
  openExternal: (url: string) => Promise<void>;
  openWindow: (targetPath: string) => Promise<void>;
  getInitialDeepLink: () => Promise<string | null>;
  onDeepLink: (callback: (url: string) => void) => () => void;
  checkForUpdates: () => Promise<ElectronUpdateInfo | null>;
  installUpdateAndRestart: () => Promise<void>;
  onUpdateEvent: (callback: (event: ElectronUpdateEvent) => void) => () => void;
};

declare global {
  interface Window {
    electron?: ElectronApi;
  }
}

export const isElectron = (): boolean => typeof window !== 'undefined' && typeof window.electron !== 'undefined';

export const isNative = (): boolean => isElectron();

/**
 * Sets the browser tab / window title. In Electron this also drives the
 * native window title, which feeds the macOS Window menu's open-windows
 * list, so windows are labelled with their file name.
 */
export const setPageTitle = (title: string): void => {
  document.title = title;
};

/**
 * Opens a URL in the user's default system browser.
 *
 * Used by the auth flow on desktop: the WorkOS authorize URL has to
 * open in the system browser so that after sign-in, the OS can redirect
 * back into the app via the `quadratic://` deep-link scheme. Opening
 * the authorize URL inside the in-app WebView would navigate the app
 * away.
 *
 * On non-native (regular browser) builds this falls back to
 * `window.open`.
 */
export const openExternalUrl = async (url: string): Promise<void> => {
  if (isElectron()) {
    await window.electron!.openExternal(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Opens an in-app route in a new native window (a full, independent
 * Quadratic instance). Only available in the Electron desktop shell,
 * which can host multiple BrowserWindows; returns false elsewhere so
 * callers can fall back to normal navigation / `window.open`.
 *
 * `targetPath` is a renderer route such as `/file/<uuid>`.
 */
export const openInNewWindow = (targetPath: string): boolean => {
  if (isElectron()) {
    void window.electron!.openWindow(targetPath);
    return true;
  }
  return false;
};
